'use client';
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var _a;
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import ReactFocusLock from 'react-focus-lock';
import { Portal } from 'react-portal';
import classnames from 'classnames';
import { oneByType } from 'react-children-by-type';
import { ModalHeader } from './ModalHeader.js';
import { ModalBody } from './ModalBody.js';
import { ModalFooter } from './ModalFooter.js';
import { ESCAPE_KEYCODE, usePrevious } from '../../index.js';
const FocusLock = (_a = ReactFocusLock.default) !== null && _a !== void 0 ? _a : ReactFocusLock;
export const Modal = (_a) => {
    var { ariaDescribedBy, ariaLabelledBy, className, isActive, children, dismissible, fullWidth, onClose } = _a, other = __rest(_a, ["ariaDescribedBy", "ariaLabelledBy", "className", "isActive", "children", "dismissible", "fullWidth", "onClose"]);
    const [isMounted, setIsMounted] = React.useState(false);
    const BODY_DISABLED_CLASS = 'sparky-body-is-disabled';
    const [activeFocus, setActiveFocus] = React.useState(isActive || false);
    const windowRef = React.useRef(null);
    const ref = React.useRef(null);
    const prevIsActive = usePrevious(isActive);
    React.useEffect(() => {
        if (prevIsActive !== undefined || null) {
            if (isActive) {
                activateDOM();
            }
            else {
                deactivateDOM();
            }
        }
    });
    React.useEffect(() => {
        setIsMounted(true);
    }, [setIsMounted]);
    function activateDOM() {
        document.body.classList.add(BODY_DISABLED_CLASS);
        setTimeout(() => {
            setActiveFocus(true);
        }, 300);
    }
    function deactivateDOM() {
        document.body.classList.remove(BODY_DISABLED_CLASS);
        setActiveFocus(false);
    }
    function handleOnClose(e) {
        deactivateDOM();
        if (onClose) {
            onClose(e);
        }
    }
    function handleOnClickOutside(e) {
        if (isActive &&
            dismissible &&
            windowRef.current &&
            !windowRef.current.contains(e.target)) {
            handleOnClose(e);
        }
    }
    function handleOnKeyDown(e) {
        if (e.code === ESCAPE_KEYCODE) {
            handleOnClose(e);
        }
    }
    const modalHeader = oneByType(children, ModalHeader);
    const header = React.Children.map(modalHeader, (child) => React.cloneElement(child, {
        onClick: (e) => handleOnClose(e),
        dismissible,
    }));
    const body = oneByType(children, ModalBody);
    const footer = oneByType(children, ModalFooter);
    const componentClassName = classnames('sparky-c-modal', className, {
        'sparky-is-active': isActive,
        'sparky-c-modal--full-screen': fullWidth,
    });
    if (!isMounted)
        return null;
    return (_jsx(Portal, { children: _jsx(FocusLock, Object.assign({ disabled: !activeFocus }, { children: _jsx("div", Object.assign({ className: componentClassName, ref: ref, "aria-hidden": !isActive, onKeyDown: (e) => handleOnKeyDown(e), onClick: (e) => handleOnClickOutside(e) }, other, { children: _jsx("article", Object.assign({ className: "sparky-c-modal__window", "aria-labelledby": ariaLabelledBy, "aria-describedby": ariaDescribedBy, ref: windowRef, role: "dialog", tabIndex: 0, "aria-modal": isActive }, { children: _jsxs("div", Object.assign({ className: "sparky-c-modal__content" }, { children: [header, body, footer] })) })) })) })) }));
};
//# sourceMappingURL=Modal.js.map